import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  TemplateRef,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { DatePipe, NgTemplateOutlet, formatDate } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';

import { PreApplicationService } from '../../../../../core/applications/pre-application.service';
import {
  DetectedAccount,
  PreApplicationDocType,
} from '../../../../../core/applications/pre-application.types';
import { DeclarationService } from '../../../../../core/entity/declaration.service';
import {
  Declaration,
  DeclaredBankAccount,
  FourState,
  HOLDER_RELATIONSHIP_LABELS,
  HolderRelationship,
} from '../../../../../core/entity/declaration.types';
import { readError } from '../../../../../core/http/read-error';
import { IconComponent } from '../../../../../shared/icon/icon';
import { DocDropComponent, DocUpload } from '../../../shared/doc-drop/doc-drop';
import { statementCoverage, StatementPeriod } from '../bank-statement-coverage';
import { CHECKLIST_DOCS, requirementLabel } from '../intake-checklist';

/** The statements' printed periods, earliest first — "31 May – 30 Jun 2025". */
function periodsOf(statements: StatementRow[]): string[] {
  return statements
    .map((s) => s.face)
    .filter((f): f is DetectedAccount => !!f?.period_start && !!f?.period_end)
    .sort((a, b) => (a.period_start ?? '').localeCompare(b.period_start ?? ''))
    .map(
      (f) =>
        `${formatDate(f.period_start as string, 'd MMM', 'en-ZA')} – ` +
        `${formatDate(f.period_end as string, 'd MMM yyyy', 'en-ZA')}`,
    );
}

/** One uploaded statement as the account review shows it (FC-DET-047). */
export interface StatementRow {
  uploadId: string;
  docShortId: string;
  fileName: string;
  /** What the statement says about itself, or `null` until something read it. */
  face: DetectedAccount | null;
  /** The upload check has not landed — the account may still be read. */
  pending: boolean;
}

/**
 * One account on the derived list with the statements filed against it.
 * ONE entry per bank account (the declaration is the first in force for it).
 */
export interface AccountGroup {
  row: Declaration;
  account: DeclaredBankAccount;
  statements: StatementRow[];
  /** The statements' own periods, earliest first, as printed — never a count against a rule. */
  periods: string[];
  /** The printed holder is not the business and nobody has said whose it is. */
  holderQuestion: boolean;
}

/**
 * Step 1 of the intake: the statements' upload and the review of the accounts
 * read off them. The intake shell keeps the uploads and the verdict loop; this
 * owns the declared list and every act on it.
 *
 * Mounted for the life of the wizard and shown only while `active`: what the
 * client has half-typed here must survive a trip to Step 2 and back, and the
 * list is read once, not on every return.
 */
@Component({
  selector: 'app-bank-accounts-step',
  imports: [
    DatePipe,
    NgTemplateOutlet,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
    IconComponent,
    DocDropComponent,
  ],
  templateUrl: './bank-accounts-step.html',
  styleUrl: './bank-accounts-step.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BankAccountsStepComponent implements OnInit {
  private readonly preApp = inject(PreApplicationService);
  private readonly declarations = inject(DeclarationService);
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  /** This is the step on screen. */
  readonly active = input.required<boolean>();
  readonly entityId = input.required<string>();
  readonly preAppId = input.required<string | null>();
  /** The bank-statement bucket, in upload order. */
  readonly uploads = input.required<DocUpload<PreApplicationDocType>[]>();
  readonly deletingIds = input.required<Set<string>>();
  /** docShortId → what the statement says about itself (null until read). */
  readonly faces = input.required<Record<string, DetectedAccount | null>>();
  /** docShortId → the declared account it's attributed to (or `null`). */
  readonly attributions = model.required<Record<string, string | null>>();
  /** The verdict poll gave up with a check still pending. */
  readonly verdictPollTimedOut = input.required<boolean>();
  /** The AI-processing consent box, which the shell also renders on Step 2. */
  readonly consentBlock = input<TemplateRef<unknown> | null>(null);

  readonly filesPicked = output<File[]>();
  readonly remove = output<string>();
  readonly next = output<void>();
  /** A correction moved statements server-side: the shell re-reads the documents. */
  readonly documentsChanged = output<void>();

  protected readonly BANK_DOCS = CHECKLIST_DOCS.filter((d) => d.step === 'bank');
  protected readonly requirementLabel = requirementLabel;

  constructor() {
    // Hand over to the accounts card the moment it opens, but not when a return
    // to the page finds it open already.
    let wasLocked = false;
    effect(() => {
      const open = this.accountsOpen();
      if (wasLocked && open && this.active()) untracked(() => this.goToAccounts());
      wasLocked = this.showAccountReview() && !open;
    });
  }

  ngOnInit(): void {
    this.loadDeclarations();
  }

  // --- Declared bank accounts (FLOW-243) -------------------------------------
  //
  // Step 1 is where the client tells us which accounts the business operates,
  // attributes each statement to one of them, and then AFFIRMS that the list is
  // complete. Three acts, in that order, and the third is deliberately its own
  // act rather than a consequence of the first two — see `affirm()`.

  /** Every declaration read back for this business (all kinds, live and withdrawn). */
  protected readonly declarations_ = signal<Declaration[]>([]);
  protected readonly accountsLoading = signal(true);
  protected readonly accountsError = signal(false);
  protected readonly showAccountForm = signal(false);
  protected readonly isAddingAccount = signal(false);
  protected readonly accountError = signal<string | null>(null);
  /**
   * Not an error — the client asked for an account to be on the list and it is.
   * Saying nothing would be worse than either: the form would close, no row
   * would appear, and the client would reasonably conclude the save failed.
   */
  protected readonly accountNote = signal<string | null>(null);
  protected readonly isAffirming = signal(false);

  /**
   * The accounts currently on the list. Keyed on `is_in_force` — DECLARED *and
   * nothing has withdrawn it*. A withdrawn row keeps `status: 'DECLARED'`
   * because the register is append-only, so filtering on the status would show
   * a retracted account forever.
   */
  protected readonly declaredAccounts = computed(() =>
    this.declarations_().filter((d) => d.kind === 'ACCOUNT' && d.is_in_force),
  );

  /**
   * The same accounts, one entry per ACCOUNT — what the per-statement select
   * offers.
   *
   * 🚨 **Distinct on the `bank_accounts` row, not on the declaration.** An
   * account declared twice is two declarations of ONE account, and the select
   * offered it twice: two options with identical text and an identical value,
   * so the client had to pick between two things that were the same and the
   * screen looked like it had lost track of their accounts. The LIST above is
   * deliberately still per-declaration — each row's Withdraw acts on a
   * declaration, so collapsing the list would hide a row nobody could then
   * withdraw.
   *
   * A declaration with no resolved account is dropped rather than shown: the
   * option's value IS the account's short_id, so such an entry would be an
   * option that cannot attribute anything.
   */
  protected readonly distinctDeclaredAccounts = computed(() => {
    const seen = new Set<string>();
    return this.declaredAccounts().filter((row) => {
      const id = row.bank_account?.short_id;
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  });

  /** The live affirmation, or `null` — the record of who said the list is complete. */
  protected readonly affirmation = computed(
    () =>
      this.declarations_().find((d) => d.kind === 'ACCOUNT_AFFIRMATION' && d.is_in_force) ?? null,
  );

  /**
   * A previous affirmation that the client's own later change retracted. Shown
   * so the withdrawal is visible rather than silent: the server withdraws the
   * affirmation when an account is added, and a client who is not told that
   * would believe a confirmation still stands that does not.
   */
  protected readonly affirmationWasWithdrawn = computed(
    () =>
      this.affirmation() === null &&
      this.declarations_().some((d) => d.kind === 'ACCOUNT_AFFIRMATION'),
  );

  /**
   * Why the add form is open: to NAME an account whose statements were not
   * uploaded (FC-DET-047's named limb — with the reason), or to add one by
   * hand for a statement nothing could read. Same form, one extra field.
   */
  protected readonly accountFormMode = signal<'named' | 'manual'>('named');

  protected readonly accountForm = this.fb.group({
    bank_name: [''],
    branch_code: [''],
    account_number: [''],
    account_holder_name: [''],
    not_uploaded_reason: [''],
    /**
     * FOUR states: yes / no / not sure / nothing selected (''). 🚨 Never a
     * checkbox — an unticked box means the client said "no trading on this
     * account", which is a substantive claim and not the same as not answering.
     */
    carries_trading_activity: ['' as FourState],
  });

  protected readonly tradingStates: { value: FourState; label: string }[] = [
    { value: 'YES', label: 'Yes' },
    { value: 'NO', label: 'No' },
    { value: 'NOT_SURE', label: 'Not sure' },
  ];

  /** The bank statements the client has uploaded — each one needs an account. */
  protected readonly bankStatementUploads = computed(() =>
    this.uploads().filter((u) => u.doc !== null),
  );

  /** Statements still waiting to be attributed — the step's soft nudge. */
  protected readonly unattributedCount = computed(
    () =>
      this.bankStatementUploads().filter((u) => !this.attributions()[u.doc?.short_id ?? '']).length,
  );

  protected readonly canAffirm = computed(
    () => this.declaredAccounts().length > 0 && this.affirmation() === null && this.accountsOpen(),
  );

  // --- The derived list, reviewed (FC-DET-047, Constitution v2.63) ---
  //
  // The client uploads statements; the system reads the account off each
  // statement's own face and declares it; the client reviews the GROUPS —
  // confirms, says whose account it is, corrects a misreading, names an
  // account it has not uploaded — and then confirms the list is complete.
  // Nothing here is a fault state: an unread statement waits, it does not fail.

  private statementRow(u: DocUpload<PreApplicationDocType>): StatementRow {
    const docShortId = u.doc?.short_id ?? '';
    return {
      uploadId: u.id,
      docShortId,
      fileName: u.fileName,
      face: this.faces()[docShortId] ?? null,
      pending: (u.doc?.assess_status ?? 'PENDING') === 'PENDING',
    };
  }

  /** The list as the client reviews it: one group per account, statements nested. */
  protected readonly accountGroups = computed<AccountGroup[]>(() => {
    const byAccount = new Map<string, StatementRow[]>();
    for (const u of this.bankStatementUploads()) {
      const accountId = this.attributions()[u.doc?.short_id ?? ''];
      if (!accountId) continue;
      const list = byAccount.get(accountId) ?? [];
      list.push(this.statementRow(u));
      byAccount.set(accountId, list);
    }
    return this.distinctDeclaredAccounts().map((row) => {
      const account = row.bank_account as DeclaredBankAccount;
      const statements = byAccount.get(account.short_id) ?? [];
      return {
        row,
        account,
        statements,
        periods: periodsOf(statements),
        holderQuestion: !!row.derived && !row.holder_relationship,
      };
    });
  });

  /** Statements nothing has attributed yet — read still pending, or unreadable. */
  protected readonly undetectedStatements = computed<StatementRow[]>(() =>
    this.bankStatementUploads()
      .filter((u) => !this.attributions()[u.doc?.short_id ?? ''])
      .map((u) => this.statementRow(u)),
  );

  /** Statements the check is still working through; a timed-out poll no longer counts. */
  protected readonly readingStatements = computed(() =>
    this.verdictPollTimedOut() ? [] : this.undetectedStatements().filter((s) => s.pending),
  );

  /** Statements read without an account: the client can match these by hand. */
  protected readonly unmatchedStatements = computed(() => {
    const reading = new Set(this.readingStatements());
    return this.undetectedStatements().filter((s) => !reading.has(s));
  });

  /**
   * Per-account guidance on the months uploaded: what is on file and which months
   * to add. Only the business's own accounts, because a director's account isn't
   * one the client is asked to cover (FC-DET-047). It decides no condition, but a
   * shortfall on the business's own account holds the accounts card (`accountsOpen`).
   */
  protected readonly statementGuidance = computed(() => {
    const groups = this.accountGroups().filter(
      (g) =>
        g.statements.length > 0 &&
        (!g.row.holder_relationship || g.row.holder_relationship === 'BUSINESS'),
    );
    return groups.flatMap((g) => {
      const periods = g.statements
        .map((s) => s.face)
        .filter((f): f is DetectedAccount => !!f?.period_start && !!f?.period_end)
        .map<StatementPeriod>((f) => ({
          start: f.period_start as string,
          end: f.period_end as string,
        }));
      const coverage = statementCoverage(periods, new Date());
      if (!coverage) return [];
      return [
        {
          id: g.row.short_id,
          account: groups.length > 1 ? this.accountLabel(g.row) : null,
          ...coverage,
        },
      ];
    });
  });

  /** Read statements that the guidance can't count: no account, or no printed dates. */
  protected readonly uncountedStatementCount = computed(() => {
    const undated = this.accountGroups()
      .flatMap((g) => g.statements)
      .filter((s) => !s.pending && !(s.face?.period_start && s.face?.period_end));
    return undated.length + this.unmatchedStatements().length;
  });

  /** Step 1's first card, by what the uploads say: nothing on file is the only "required". */
  protected readonly bankUploadTag = computed(() => {
    if (this.bankStatementUploads().length === 0) return null;
    if (this.statementsSettling()) return 'Reading';
    if (this.problemStatements().length > 0 || this.failedUploads().length > 0) {
      return 'Needs fixing';
    }
    const guidance = this.statementGuidance();
    if (guidance.some((g) => g.needsMore)) return 'Add more';
    // "Done" is a claim about the months, so it needs months we could actually read.
    return guidance.length > 0 && this.uncountedStatementCount() === 0 ? 'Done' : 'Uploaded';
  });

  /** Step 1's second card: never a warning, because an unconfirmed list is not a fault. */
  protected readonly accountsTag = computed(() => {
    if (this.affirmation()) return 'Confirmed';
    if (this.statementsSettling()) return 'Reading';
    return this.accountsOpen() ? 'To do' : 'Waiting';
  });

  /** Statements the upload check flagged as wrong: not a bank statement, or not usable. */
  protected readonly problemStatements = computed(() =>
    this.bankStatementUploads()
      .filter((u) => u.doc?.assess_status === 'PROBLEM')
      .map((u) => u.fileName),
  );

  protected readonly failedUploads = computed(() =>
    this.uploads()
      .filter((u) => u.state === 'error')
      .map((u) => u.fileName),
  );

  /**
   * What the client is asked to fix before the accounts open, as one comparable
   * value. Months count only on an account known to be the business's: an account
   * still awaiting "whose is this?" can only be answered inside the card it would hold.
   */
  private readonly statementIssues = computed(() => {
    const ownAccounts = new Set(
      this.accountGroups()
        .filter((g) => g.row.holder_relationship === 'BUSINESS')
        .map((g) => g.row.short_id),
    );
    return [
      ...this.problemStatements(),
      ...this.failedUploads(),
      ...this.statementGuidance()
        .filter((g) => g.needsMore && ownAccounts.has(g.id))
        .map((g) => `${g.id}: ${g.message}`),
    ].join('\n');
  });

  /**
   * The issues the client chose to carry past. They can't always be fixed: a young
   * account has no older months, and the check can be wrong about a file. A new issue
   * asks again. Held for this visit only: it records nothing and decides no condition.
   */
  private readonly issuesCarriedPast = signal('');

  protected carryOn(): void {
    this.issuesCarriedPast.set(this.statementIssues());
  }

  /** The statements are read, and something on them still wants the client's attention. */
  protected readonly statementsNeedAttention = computed(
    () =>
      !this.statementsSettling() &&
      this.statementIssues() !== '' &&
      this.statementIssues() !== this.issuesCarriedPast(),
  );

  /** The accounts card opens once every statement is read and nothing is left to fix. */
  protected readonly accountsOpen = computed(
    () => this.showAccountReview() && !this.statementsSettling() && !this.statementsNeedAttention(),
  );

  private readonly accountsHeading = viewChild<ElementRef<HTMLElement>>('accountsHeading');

  /** Bring the accounts card into view, when it opens or on "Next: check your accounts". */
  protected goToAccounts(): void {
    const heading = this.accountsHeading()?.nativeElement;
    heading?.scrollIntoView({ block: 'start' });
    heading?.focus({ preventScroll: true });
  }

  /** The directors and related parties a holder can be linked to. */
  protected readonly partyOptions = computed(() =>
    this.declarations_()
      .filter((d) => (d.kind === 'DIRECTOR' || d.kind === 'RELATED_PARTY') && d.is_in_force)
      .map((d) => ({ short_id: d.short_id, label: d.full_name || d.party_name || d.short_id })),
  );

  protected readonly holderChoices: { value: Exclude<HolderRelationship, ''>; label: string }[] = (
    Object.keys(HOLDER_RELATIONSHIP_LABELS) as Exclude<HolderRelationship, ''>[]
  ).map((value) => ({ value, label: HOLDER_RELATIONSHIP_LABELS[value] }));

  protected holderLabel(row: Declaration): string {
    const rel = row.holder_relationship;
    return rel ? HOLDER_RELATIONSHIP_LABELS[rel] : '';
  }

  /** The client's answer to "whose account is this?", per group, before it is saved. */
  protected readonly holderAnswer = signal<Record<string, HolderRelationship>>({});
  protected readonly holderPartyAnswer = signal<Record<string, string | null>>({});
  protected readonly savingGroupId = signal<string | null>(null);

  protected setHolderAnswer(rowShortId: string, value: HolderRelationship): void {
    this.holderAnswer.update((m) => ({ ...m, [rowShortId]: value }));
  }

  protected setHolderParty(rowShortId: string, value: string | null): void {
    this.holderPartyAnswer.update((m) => ({ ...m, [rowShortId]: value }));
  }

  /**
   * Record whose account a derived one is. Goes through the correction door
   * WITHOUT the identity, so the number the statement supplied stands and the
   * client never re-types it; the derived row is withdrawn and the answered
   * row appended (append-only, the derived reading stays readable).
   */
  protected answerHolder(group: AccountGroup): void {
    const relationship = this.holderAnswer()[group.row.short_id];
    if (!relationship) return;
    const party = this.holderPartyAnswer()[group.row.short_id] ?? null;
    this.savingGroupId.set(group.row.short_id);
    this.accountError.set(null);
    this.declarations
      .correct(this.entityId(), group.row.short_id, {
        holder_relationship: relationship,
        holder_party: relationship === 'BUSINESS' || relationship === 'OTHER' ? null : party,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.savingGroupId.set(null);
          this.loadDeclarations();
        },
        error: (err: unknown) => {
          this.savingGroupId.set(null);
          this.accountError.set(readError(err));
          this.cdr.markForCheck();
        },
      });
  }

  // --- "This isn't right": correct what the system read ---
  protected readonly correctingId = signal<string | null>(null);
  protected readonly correctionForm = this.fb.group({
    bank_name: [''],
    branch_code: [''],
    account_number: [''],
    account_holder_name: [''],
  });

  protected startCorrection(group: AccountGroup): void {
    this.accountError.set(null);
    this.correctionForm.reset({
      bank_name: group.account.bank_name,
      branch_code: group.account.branch_code,
      account_number: '',
      account_holder_name: group.row.holder_name || group.account.account_holder_name,
    });
    this.correctingId.set(group.row.short_id);
  }

  protected cancelCorrection(): void {
    this.correctingId.set(null);
  }

  protected submitCorrection(): void {
    const id = this.correctingId();
    if (!id) return;
    const value = this.correctionForm.getRawValue();
    const bank = (value.bank_name ?? '').trim();
    const number = (value.account_number ?? '').trim();
    const holder = (value.account_holder_name ?? '').trim();
    if (!bank || !number || !holder) {
      this.accountError.set('Fill in the bank, the full account number and the account holder.');
      return;
    }
    this.savingGroupId.set(id);
    this.accountError.set(null);
    this.declarations
      .correct(this.entityId(), id, {
        bank_name: bank,
        branch_code: (value.branch_code ?? '').trim(),
        account_number: number,
        account_holder_name: holder,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.savingGroupId.set(null);
          this.correctingId.set(null);
          // The statements followed the corrected account server-side; re-read
          // both the list and the attributions rather than guessing.
          this.loadDeclarations();
          this.documentsChanged.emit();
        },
        error: (err: unknown) => {
          this.savingGroupId.set(null);
          this.accountError.set(readError(err));
          this.cdr.markForCheck();
        },
      });
  }

  /** Re-read the list; the shell calls this when an upload or verdict brings an account. */
  loadDeclarations(): void {
    this.accountsLoading.set(true);
    this.accountsError.set(false);
    this.declarations
      .list(this.entityId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp) => {
          this.declarations_.set(resp.declarations);
          this.accountsLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.accountsError.set(true);
          this.accountsLoading.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  protected retryDeclarations(): void {
    this.loadDeclarations();
  }

  protected toggleAccountForm(mode: 'named' | 'manual' = 'named'): void {
    this.accountError.set(null);
    this.accountNote.set(null);
    this.accountForm.reset({
      bank_name: '',
      branch_code: '',
      account_number: '',
      account_holder_name: '',
      not_uploaded_reason: '',
      carries_trading_activity: '',
    });
    const reopen = !this.showAccountForm() || this.accountFormMode() !== mode;
    this.accountFormMode.set(mode);
    this.showAccountForm.set(reopen);
  }

  protected addAccount(): void {
    const value = this.accountForm.getRawValue();
    const bank = (value.bank_name ?? '').trim();
    const branch = (value.branch_code ?? '').trim();
    const number = (value.account_number ?? '').trim();
    const holder = (value.account_holder_name ?? '').trim();
    const reason = (value.not_uploaded_reason ?? '').trim();
    if (!bank || !number || !holder) {
      this.accountError.set('Fill in the bank, the account number and the account holder.');
      return;
    }
    if (this.accountFormMode() === 'named' && !reason) {
      this.accountError.set("Say why you haven't uploaded statements for this account.");
      return;
    }
    this.isAddingAccount.set(true);
    this.accountError.set(null);
    this.accountNote.set(null);
    this.declarations
      .declare(this.entityId(), {
        kind: 'ACCOUNT',
        bank_name: bank,
        branch_code: branch,
        account_number: number,
        account_holder_name: holder,
        carries_trading_activity: (value.carries_trading_activity ?? '') as FourState,
        not_uploaded_reason: this.accountFormMode() === 'named' ? reason : '',
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        // Re-read the whole list rather than appending the new row. Adding an
        // account WITHDRAWS a standing affirmation server-side, so the response
        // to this one POST is not the whole change — appending would leave a
        // retracted confirmation on screen looking current.
        next: (row) => {
          this.isAddingAccount.set(false);
          this.showAccountForm.set(false);
          // The server appended nothing because the account was already on the
          // list (200, not 201). Told plainly rather than swallowed: the form
          // closes and no new row appears, so silence reads as a failed save.
          if (row.already_declared) {
            this.accountNote.set('That account is already on your list, so nothing was added.');
          }
          this.loadDeclarations();
        },
        error: (err: unknown) => {
          this.accountError.set(readError(err));
          this.isAddingAccount.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  protected withdrawAccount(row: Declaration): void {
    this.accountError.set(null);
    this.declarations
      .withdraw(this.entityId(), row.short_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.loadDeclarations(),
        error: (err: unknown) => {
          this.accountError.set(readError(err));
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * The client states that the declared list is every account the business
   * operates.
   *
   * 🚨 **Its own act, never a default-on checkbox.** One declared account is
   * not every account: a client who lists their quietest and withholds three
   * others satisfies any count. What makes this meaningful is that it is a
   * statement nobody can make by accident — so it is a button the client
   * presses, with the wording on the button, and it is withdrawable.
   */
  protected affirm(): void {
    this.isAffirming.set(true);
    this.accountError.set(null);
    this.declarations
      .declare(this.entityId(), { kind: 'ACCOUNT_AFFIRMATION' })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isAffirming.set(false);
          this.loadDeclarations();
        },
        error: (err: unknown) => {
          this.accountError.set(readError(err));
          this.isAffirming.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  protected withdrawAffirmation(): void {
    const current = this.affirmation();
    if (!current) return;
    this.declarations
      .withdraw(this.entityId(), current.short_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.loadDeclarations(),
        error: (err: unknown) => {
          this.accountError.set(readError(err));
          this.cdr.markForCheck();
        },
      });
  }

  protected accountLabel(row: Declaration): string {
    const acc = row.bank_account;
    if (!acc) return '—';
    return `${acc.bank_name} ····${acc.account_number_tail}`;
  }

  protected attributionFor(docShortId: string): string | null {
    return this.attributions()[docShortId] ?? null;
  }

  /** Attribute one uploaded statement to a declared account (the §2.A.13 join). */
  protected onAttributionChange(docShortId: string, accountShortId: string | null): void {
    const id = this.preAppId();
    if (!id) return;
    const previous = this.attributions();
    this.attributions.set({ ...previous, [docShortId]: accountShortId });
    this.preApp
      .attributeDocument(id, docShortId, accountShortId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: (err: unknown) => {
          // Roll the select back: leaving it showing an account the server
          // refused would be the screen asserting an attribution that does not
          // exist.
          this.attributions.set(previous);
          this.accountError.set(readError(err));
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * A statement is still uploading or being checked. A check the poll gave up on
   * counts as finished, so a slow one can't strand the client on this step.
   */
  protected readonly statementsSettling = computed(() =>
    this.uploads().some(
      (u) =>
        u.state === 'uploading' ||
        (u.state === 'done' &&
          (u.doc?.assess_status ?? 'PENDING') === 'PENDING' &&
          !this.verdictPollTimedOut()),
    ),
  );

  /**
   * The account card appears with the first statement on file, so the page does
   * not jump when a slow one lands. The list is read off the statements
   * (FC-DET-047), so it can still grow: `accountsOpen` holds it until they settle.
   */
  protected readonly showAccountReview = computed(() => this.bankStatementUploads().length > 0);

  /** Step 1 is finished: the accounts card is open and the client confirmed the list. */
  protected readonly canLeaveBankStep = computed(
    () => this.accountsOpen() && this.affirmation() !== null,
  );

  /** Why Step 1's forward button is greyed out, or `null` when it isn't. */
  protected readonly bankStepBlockedReason = computed(() => {
    if (this.canLeaveBankStep()) return null;
    if (this.bankStatementUploads().length === 0 && !this.statementsSettling()) {
      return 'Upload your bank statements to continue.';
    }
    if (this.statementsSettling()) {
      return "We're still reading your statements. You can confirm your accounts once that's done.";
    }
    if (this.statementsNeedAttention()) {
      return 'Sort out the statements flagged above, or carry on with what you’ve uploaded.';
    }
    return 'Check your accounts and confirm the list to continue.';
  });
}
